import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'

const BASE = import.meta.env.VITE_API_URL || ''

const AuthContext = createContext(null)

export function useAuth() {
  return useContext(AuthContext)
}

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetch(`${BASE}/api/auth/me`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => setUser(data.user || null))
      .catch(() => setUser(null))
      .finally(() => setLoading(false))
  }, [])

  const login = useCallback(async (email, password) => {
    const res  = await fetch(`${BASE}/api/auth/login`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Login failed')
    setUser(data.user)
    return data.user
  }, [])

  const register = useCallback(async (displayName, email, password, inviteCode) => {
    const res  = await fetch(`${BASE}/api/auth/register`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ displayName, email, password, inviteCode }),
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Registration failed')
    setUser(data.user)
    return data.user
  }, [])

  const logout = useCallback(async () => {
    await fetch(`${BASE}/api/auth/logout`, { method: 'POST', credentials: 'include' })
    setUser(null)
  }, [])

  const requestMagicLink = useCallback(async (email) => {
    const res  = await fetch(`${BASE}/api/auth/magic-link`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Failed to send magic link')
    return data
  }, [])

  const updateAccount = useCallback(async (payload) => {
    const res  = await fetch(`${BASE}/api/auth/account`, {
      method: 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Update failed')
    setUser(data.user)
    return data.user
  }, [])

  const deleteAccount = useCallback(async () => {
    const res  = await fetch(`${BASE}/api/auth/account`, {
      method: 'DELETE', credentials: 'include',
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Failed to delete account')
    setUser(null)
  }, [])

  return (
    <AuthContext.Provider value={{
      user, loading,
      login, register, logout,
      requestMagicLink, updateAccount, deleteAccount,
    }}>
      {children}
    </AuthContext.Provider>
  )
}
